import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Play, BookOpen, Trophy, Users, BarChart3, Tv, FileText, Sparkles, Target, GraduationCap } from 'lucide-react';
import { FieldDiagram, PlayerDot } from '../components/FieldDiagram';
import { blogArticles } from '../data/blogArticles';

const Home = () => {
  const features = [
    {
      icon: BookOpen,
      title: "Reglas Básicas",
      description: "Aprende cómo funciona el campo, los downs y cómo se anotan puntos.",
      link: "/reglas",
      color: "#22c55e"
    },
    {
      icon: Users,
      title: "Posiciones",
      description: "Descubre qué hace cada jugador en el campo y sus roles específicos.",
      link: "/posiciones",
      color: "#3b82f6"
    },
    {
      icon: Trophy,
      title: "Estrategia",
      description: "Entiende las formaciones, jugadas y tácticas como un entrenador.",
      link: "/estrategia",
      color: "#f97316"
    },
    {
      icon: BarChart3,
      title: "Estadísticas",
      description: "Domina las stats tradicionales y las métricas avanzadas modernas.",
      link: "/estadisticas",
      color: "#8b5cf6"
    }
  ];

  // Sample formation for the hero field
  const heroFormation = [
    { position: "C", x: 50, y: 65 },
    { position: "LG", x: 40, y: 65 },
    { position: "RG", x: 60, y: 65 },
    { position: "LT", x: 30, y: 65 },
    { position: "RT", x: 70, y: 65 },
    { position: "QB", x: 50, y: 50, isQB: true },
    { position: "RB", x: 50, y: 38 },
    { position: "WR", x: 10, y: 65 },
    { position: "WR", x: 90, y: 65 },
    { position: "TE", x: 80, y: 65 }
  ];

  return (
    <div className="grain-overlay" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: 'url(https://images.unsplash.com/photo-1565789426992-9c335aa0a62c?auto=format&fit=crop&w=2000&q=80)'
          }}
        />
        <div className="hero-gradient absolute inset-0" />
        
        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Text */}
            <div className="text-left stagger-children">
              <span 
                className="inline-block px-4 py-2 bg-emerald-500/20 text-emerald-400 text-sm font-bold uppercase tracking-widest rounded-full mb-6"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                La guía definitiva
              </span>
              
              <h1 
                className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-extrabold text-white uppercase tracking-tighter leading-none mb-6"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                Guía NFL<br />
                <span className="text-emerald-400">en Español</span>
              </h1>
              
              <p className="text-lg md:text-xl text-slate-300 max-w-xl mb-8 leading-relaxed">
                De cero a experto. Aprende fútbol americano desde las reglas básicas hasta el análisis avanzado, explicado en claro castellano.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link 
                  to="/que-es-nfl"
                  className="btn-primary inline-flex items-center justify-center gap-2 text-lg"
                  data-testid="cta-beginner"
                >
                  <Play className="w-5 h-5" />
                  Soy Principiante
                </Link>
                <Link 
                  to="/estrategia"
                  className="btn-secondary inline-flex items-center justify-center gap-2 text-lg"
                  data-testid="cta-advanced"
                >
                  Ya conozco la NFL
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            </div>
            
            {/* Right Column - Field Diagram */}
            <div className="hidden lg:block animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
              <div className="glow-green rounded-2xl overflow-hidden">
                <FieldDiagram>
                  {heroFormation.map((player, index) => (
                    <PlayerDot 
                      key={index}
                      x={player.x}
                      y={player.y}
                      position={player.position}
                      isQB={player.isQB}
                      showLabel={false}
                    />
                  ))}
                </FieldDiagram>
              </div>
              <p className="text-center text-slate-500 text-sm mt-4 italic">
                Formación ofensiva básica con 11 jugadores
              </p>
            </div>
          </div>
        </div>
        
        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 rounded-full border-2 border-slate-600 flex items-start justify-center p-2">
            <div className="w-1 h-2 bg-slate-400 rounded-full" />
          </div>
        </div>
      </section>

      {/* How to use section */}
      <section className="py-20 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span 
              className="text-emerald-400 text-sm font-bold uppercase tracking-widest"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Cómo usar esta guía
            </span>
            <h2 
              className="text-3xl md:text-5xl font-bold text-white mt-4 uppercase tracking-tight"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              3 pasos para dominar la NFL
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: "01",
                title: "Lee las reglas básicas",
                description: "Empieza por entender el campo, los downs y cómo se anotan puntos. Es la base de todo."
              },
              {
                step: "02",
                title: "Ve un partido tomando notas",
                description: "Aplica lo aprendido viendo un partido real. Identifica posiciones y jugadas."
              },
              {
                step: "03",
                title: "Pasa al nivel avanzado",
                description: "Una vez domines lo básico, sumérgete en estrategia, formaciones y estadísticas."
              }
            ].map((item, index) => (
              <div 
                key={index}
                className="relative group"
              >
                <div className="absolute -top-4 -left-4 text-8xl font-black text-slate-800/50 group-hover:text-emerald-500/20 transition-colors duration-300" 
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                >
                  {item.step}
                </div>
                <div className="relative tactical-card p-8 rounded-r-xl h-full">
                  <h3 
                    className="text-xl font-bold text-white mb-3 uppercase"
                    style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                  >
                    {item.title}
                  </h3>
                  <p className="text-slate-400 leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span 
              className="text-orange-400 text-sm font-bold uppercase tracking-widest"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Contenido completo
            </span>
            <h2 
              className="text-3xl md:text-5xl font-bold text-white mt-4 uppercase tracking-tight"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Todo lo que necesitas saber
            </h2>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <Link 
                key={index}
                to={feature.link}
                className="info-card group cursor-pointer"
                data-testid={`feature-card-${feature.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div 
                  className="w-14 h-14 rounded-xl flex items-center justify-center mb-4 transition-transform duration-300 group-hover:scale-110"
                  style={{ backgroundColor: `${feature.color}20` }}
                >
                  <feature.icon className="w-7 h-7" style={{ color: feature.color }} />
                </div>
                <h3 
                  className="text-xl font-bold text-white mb-2 uppercase group-hover:text-emerald-400 transition-colors duration-300"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                >
                  {feature.title}
                </h3>
                <p className="text-slate-400 text-sm leading-relaxed">{feature.description}</p>
                <div className="flex items-center gap-2 mt-4 text-slate-500 group-hover:text-emerald-400 transition-colors duration-300">
                  <span className="text-sm font-medium">Explorar</span>
                  <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform duration-300" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ 
            backgroundImage: 'url(https://images.unsplash.com/photo-1663563624897-de8972d7ce93?auto=format&fit=crop&w=2000&q=80)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/95 to-slate-950" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Tv className="w-16 h-16 text-orange-500 mx-auto mb-6" />
          <h2 
            className="text-3xl md:text-5xl font-bold text-white uppercase tracking-tight mb-6"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            ¿Listo para ver tu primer partido?
          </h2>
          <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto">
            Descubre dónde ver la NFL en español, consejos para principiantes y contenido recomendado para disfrutar al máximo.
          </p>
          <Link 
            to="/como-ver"
            className="btn-primary inline-flex items-center gap-2 text-lg"
            data-testid="cta-how-to-watch"
          >
            Cómo ver la NFL
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Empieza Aquí - Beginner Section */}
      <section className="py-20 bg-gradient-to-b from-slate-900/50 to-transparent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span 
              className="inline-flex items-center gap-2 text-emerald-400 text-sm font-bold uppercase tracking-widest mb-4"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              <GraduationCap className="w-5 h-5" />
              Para Principiantes
            </span>
            <h2 
              className="text-3xl md:text-5xl font-bold text-white uppercase tracking-tight"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Empieza aquí si eres nuevo
            </h2>
            <p className="text-slate-400 mt-4 max-w-2xl mx-auto">
              ¿Primera vez con el fútbol americano? Sigue este camino de aprendizaje diseñado para llevarte de cero a fan.
            </p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-4">
            {[
              {
                step: 1,
                title: "Qué es la NFL",
                desc: "Conoce la liga, los equipos y cómo funciona",
                link: "/blog/que-es-la-nfl-guia-completa",
                icon: Target
              },
              {
                step: 2,
                title: "Las Reglas Básicas",
                desc: "Downs, puntuación y lo esencial",
                link: "/blog/reglas-nfl-explicadas-facil",
                icon: BookOpen
              },
              {
                step: 3,
                title: "Las Posiciones",
                desc: "Quién hace qué en el campo",
                link: "/blog/posiciones-nfl-guia-completa",
                icon: Users
              },
              {
                step: 4,
                title: "Ve tu primer partido",
                desc: "Consejos y dónde verlo",
                link: "/blog/como-entender-futbol-americano-desde-cero",
                icon: Play
              }
            ].map((item, index) => (
              <Link 
                key={index}
                to={item.link}
                className="group relative p-6 bg-slate-800/50 rounded-xl border border-slate-700 hover:border-emerald-500/50 transition-all duration-300"
              >
                <div className="absolute -top-3 -left-3 w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center text-slate-900 font-bold text-sm">
                  {item.step}
                </div>
                <item.icon className="w-8 h-8 text-emerald-400 mb-3 group-hover:scale-110 transition-transform" />
                <h3 
                  className="text-lg font-bold text-white mb-1 group-hover:text-emerald-400 transition-colors"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                >
                  {item.title}
                </h3>
                <p className="text-slate-400 text-sm">{item.desc}</p>
                <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-emerald-400 group-hover:translate-x-1 transition-all absolute bottom-4 right-4" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Articles Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-12">
            <div>
              <span 
                className="inline-flex items-center gap-2 text-orange-400 text-sm font-bold uppercase tracking-widest mb-2"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                <FileText className="w-4 h-4" />
                Blog
              </span>
              <h2 
                className="text-3xl md:text-4xl font-bold text-white uppercase tracking-tight"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                Artículos destacados
              </h2>
            </div>
            <Link 
              to="/blog"
              className="hidden md:flex items-center gap-2 text-slate-400 hover:text-emerald-400 transition-colors"
            >
              Ver todos
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {blogArticles.slice(0, 3).map((article, index) => (
              <Link 
                key={article.id}
                to={`/blog/${article.slug}`}
                className="group"
              >
                <article className="h-full flex flex-col">
                  <div className="relative overflow-hidden rounded-xl mb-4">
                    <img 
                      src={article.image} 
                      alt={article.title}
                      className="w-full aspect-video object-cover transition-transform duration-300 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute top-3 left-3">
                      <span className="px-2 py-1 bg-slate-900/80 text-emerald-400 text-xs font-bold rounded">
                        {article.category}
                      </span>
                    </div>
                  </div>
                  <h3 
                    className="text-xl font-bold text-white mb-2 group-hover:text-emerald-400 transition-colors line-clamp-2"
                    style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                  >
                    {article.title}
                  </h3>
                  <p className="text-slate-400 text-sm mb-4 line-clamp-2 flex-1">
                    {article.excerpt}
                  </p>
                  <span className="text-emerald-400 text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                    Leer artículo
                    <ArrowRight className="w-4 h-4" />
                  </span>
                </article>
              </Link>
            ))}
          </div>
          
          <div className="mt-8 text-center md:hidden">
            <Link 
              to="/blog"
              className="btn-secondary inline-flex items-center gap-2"
            >
              Ver todos los artículos
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Ad Space Placeholder */}
      {/* <section className="py-8">
        <div className="max-w-4xl mx-auto px-4">
          <div className="p-6 border-2 border-dashed border-slate-700 rounded-xl text-center text-slate-600 text-sm">
            <span className="opacity-50">Espacio publicitario</span>
          </div>
        </div>
      </section> */}

      {/* Footer */}
      <footer className="py-12 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-500 rounded-lg flex items-center justify-center">
                <span className="text-slate-900 font-black text-lg" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  NFL
                </span>
              </div>
              <div>
                <span className="text-white font-bold tracking-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  GUÍA NFL EN ESPAÑOL
                </span>
              </div>
            </div>
            <p className="text-slate-500 text-sm">
              Contenido educativo sobre fútbol americano. No afiliado con la NFL.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
